/**
 * Restores every Metro chunk that OneTrust's auto-block turned into
 * `type="text/plain"`, so lazy `import()` calls resolve.
 *
 * In production, Metro loads chunks through `<script>` tags with absolute
 * same-origin URLs, and OneTrust matches blocked sources by substring, so a
 * source scanned as our bare origin blocks every chunk and leaves its
 * `import()` pending forever. A blocked script never starts, so clearing its
 * type, marking it with `data-ot-ignore` and re-inserting it loads it with
 * Metro's `onload` and `onerror` handlers still attached.
 *
 * Must be called while the entry bundle is executing and BEFORE OneTrust's
 * scripts are appended. Idempotent.
 */
export function addLocalChunkSkip() {
  const hasAdded = state.chunkFolder !== undefined;

  if (hasAdded) {
    return;
  }

  // Development loads chunks with fetch + eval, never through script tags.
  if (__DEV__) {
    return;
  }

  const chunkFolder = getRunningBundleFolder();

  if (!chunkFolder) {
    return;
  }

  state.chunkFolder = chunkFolder;

  const observer = new MutationObserver(processMutations);

  observer.observe(document.head, { childList: true });
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Metro writes lazy chunks next to the entry bundle, and the entry bundle is
 * the script executing while the app boots.
 */
function getRunningBundleFolder() {
  const script = document.currentScript;
  const bundleSource = script instanceof HTMLScriptElement ? script.src : undefined;

  if (!bundleSource) {
    return;
  }

  return new URL('.', bundleSource).href;
}

function processMutations(mutations: MutationRecord[]) {
  mutations.forEach(processMutation);
}

function processMutation(mutation: MutationRecord) {
  mutation.addedNodes.forEach(restoreIfBlockedChunk);
}

function restoreIfBlockedChunk(node: Node) {
  if (!(node instanceof HTMLScriptElement)) {
    return;
  }

  if (!checkIfBlockedChunk(node)) {
    return;
  }

  node.setAttribute('data-ot-ignore', 'true');
  node.removeAttribute('type');

  // The browser only prepares a script again when it is re-inserted.
  node.remove();
  document.head.append(node);
}

function checkIfBlockedChunk(script: HTMLScriptElement) {
  const isBlocked = script.getAttribute('type') === 'text/plain';
  const isChunk = checkIfChunkSource(script.getAttribute('src'));

  return isBlocked && isChunk;
}

function checkIfChunkSource(source: string | null) {
  const { chunkFolder } = state;

  if (!source || !chunkFolder) {
    return false;
  }

  try {
    return new URL(source, window.location.href).href.startsWith(chunkFolder);
  } catch {
    return false;
  }
}

// ─── State ───────────────────────────────────────────────────────────────────

const state: State = { chunkFolder: undefined };

// ─── Types ───────────────────────────────────────────────────────────────────

type State = { chunkFolder: string | undefined };
